import { useState } from 'react';
import { useCreate } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { CreateApiKeyResponse } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlaintextSecretDialog } from '@/components/PlaintextSecretDialog';

export function ApiKeysCreate() {
  const navigate = useNavigate();
  const { mutate, mutation } = useCreate<CreateApiKeyResponse>();
  const isPending = mutation.isPending;
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [revealed, setRevealed] = useState<CreateApiKeyResponse | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: { name: string; userId?: string } = { name: name.trim() };
    if (userId.trim()) body.userId = userId.trim();
    mutate(
      { resource: 'api-keys', values: body, successNotification: false },
      {
        onSuccess: (res) => {
          setRevealed(res.data);
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Mint API key</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">New key</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                required
                placeholder="ci-bot, deploy-pipeline, …"
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="userId">Bind to user (optional)</Label>
              <Input
                id="userId"
                value={userId}
                placeholder="UUID — leave blank for a machine principal"
                onChange={(e) => setUserId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                A machine principal has the <code>admin</code> role and is not tied to any signed-in user.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Minting…' : 'Mint key'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/api-keys')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <PlaintextSecretDialog
        open={!!revealed}
        title="API key created"
        description={revealed ? `Prefix: ${revealed.prefix} · Name: ${revealed.name}` : ''}
        secret={revealed?.plaintext ?? null}
        onClose={() => {
          setRevealed(null);
          navigate('/admin/api-keys');
        }}
      />
    </div>
  );
}
